import React, { useEffect } from 'react';
import cn from 'classnames';
import { Tile, TileHeader, TileTabsRow } from '@creditflowresearch/uilib/Tile';
import Flex from '@creditflowresearch/uilib/Flex';
import Button from '@creditflowresearch/uilib/Button';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import { dataTableStyles } from '@creditflowresearch/uilib/Table/DataTable';
import ResearchTable from '../../common/ResearchTable';
import SearchContainer from '../../common/SearchContainer/SearchContainer';
import Tabs from '@/creditflowresearch/ui/components/research/common/Tabs';
import EmptyMessageWithLink from '@/creditflowresearch/ui/common/components/EmptyMessageWithLink';
import LogInButton from '@/creditflowresearch/ui/common/components/LogInButton';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import URL from '@/creditflowresearch/Router/urlConstants';
import { AbsProduct } from '@/creditflowresearch/domain/Product';
import { RegionAbbreviations } from '@/creditflowresearch/domain/Region';
import { useModuleVisibility } from '@/creditflowresearch/application/ModuleVisibility';
import { RESEARCH_HEADER_MAPPING_NAME } from '@/creditflowresearch/application/Research/constants';
import ResearchContextProvider, { useResearchContext } from '@/creditflowresearch/application/Research/ResearchContext';
import ResearchFilterContextProvider from '@/creditflowresearch/application/Research/ResearchFilterContext';
import { useParentPageContext } from '@/creditflowresearch/application/ParentPageContext';
import DocumentGroupContextProvider from '@/creditflowresearch/application/Research/DocumentGroupContext';
import { useResearchDocumentGroupList } from '@/creditflowresearch/application/Research/hooks/useResearchDocumentGroupList';
import { makeEmptyGroupIds } from '@/creditflowresearch/application/Research/utils';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import Paginator from './PaginatorContainer';

import ResearchTabSelect from './ResearchTabSelect';

import researchTableStyles from '@/creditflowresearch/ui/components/research/common/ResearchTable/researchTable.module.scss';
import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import tileStyles from '@creditflowresearch/uilib/Tile/tile.module.scss';
import styles from './researchTile.module.scss';

const researchUrl = `${ URL.NRSRO_RESEARCH }?products=${ AbsProduct.ABBREVIATION }&regions=${ RegionAbbreviations.USOA }`;

const ResearchTile = () => {
  const {
    researchData: { collection },
    resetResearch,
    isFetchingResearchList,
    isFetchingAllGroups,
    isChangingDocumentGroup,
  } = useResearchContext();
  const { checkAccessByUserPlan } = useUserPlanContext();
  const isModuleVisible = useModuleVisibility(RESEARCH_HEADER_MAPPING_NAME);
  const isFetching = isFetchingResearchList || isFetchingAllGroups || isChangingDocumentGroup;

  useEffect(() => resetResearch, [resetResearch]);

  if (!isModuleVisible) return null;

  return (
    <Tile dataTest="researchTile" className={ spacesStyles.mtl }>
      <CheckUserAccess
        conditions={ [{
          checks: [checkAccessByUserPlan(UserPlanType.STARTER)],
          fallback: (
            <TileHeader title={ <h3 data-test="researchTileTitle">Research</h3> } dataTest="researchTileHeader">
              <LogInButton dataTest="researchTileTitleLoginButton" withIcon={ false } variant="outline" />
            </TileHeader>
          ),
        }] }
      >
        <TileHeader title={ <h3 data-test="researchTileTitle">Research</h3> } dataTest="researchTileHeader" noDesktopBottomBorder>
          <Flex
            direction={ { sm: 'column-reverse', md: 'column-reverse', xl: 'row' } }
            align={ { sm: 'stretch', md: 'stretch', xl: 'center' } }
            gap={ 10 }
          >
            <div className={ tileStyles.headerSearch }>
              <SearchContainer />
            </div>
            <div className={ styles.mobileTabSelect }>
              <ResearchTabSelect />
            </div>
            <Button
              as="a"
              href={ researchUrl }
              target="_blank"
              variant="outline"
              size="S"
              dataTest="goToResearchLink"
            >
              All Research
            </Button>
          </Flex>
        </TileHeader>
        <TileTabsRow dataTest="researchTileTabs" overlap={ false }>
          <Tabs dataTest="researchTableTabs" isTile />
        </TileTabsRow>
        { (isFetching && !collection.length) && <Spinner overlay /> }
        <div className={ cn(dataTableStyles.dataTableContainer, researchTableStyles.dataTableContainer) }>
          { !!collection.length && <ResearchTable /> }
          <Paginator dataTest="researchTableWrapperPaginator" />
          { (!collection.length && !isFetching) && (
            <div className={ researchTableStyles.emptyMessage }>
              <EmptyMessageWithLink
                text="Explore all other"
                linkTo={ URL.NRSRO_RESEARCH_REPORTS }
                linkTitle="research"
              />
            </div>
          ) }
        </div>
      </CheckUserAccess>
    </Tile>
  );
};

export default () => {
  const { research } = useParentPageContext();
  const researchDocumentGroupList = useResearchDocumentGroupList();

  return (
    <DocumentGroupContextProvider initial={ {
      researchDocumentGroupList,
      initialGroup: research.includedDocumentGroup,
      emptyGroupIds: makeEmptyGroupIds(research.response),
    } }
    >
      <ResearchFilterContextProvider>
        <ResearchContextProvider data={ research }>
          <ResearchTile />
        </ResearchContextProvider>
      </ResearchFilterContextProvider>
    </DocumentGroupContextProvider>
  );
};
